import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery
   * with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   *
   * @param {ZoomMediaSelectedEvent} event - The zoom media event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * Zooms the selected media.
   *
   * @param {number} index - The media index.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Opens the currently selected image in the zoom dialog.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const currentIndex = this.slideshow?.current ?? 0;
    this.zoom(currentIndex, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index - The media index.
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}